import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { CanActivateChildFn, PRIMARY_OUTLET, RedirectCommand, Router, type UrlSegmentGroup } from '@angular/router';
import { AccountKindService } from '@frontendlib/common/services/account-kind.service';
import { AccountService } from '@frontendlib/common/services/account.service';
import { settingsAppsAddress, settingsPageAllowed } from '@modules/settings-window/settings-menu';
import { SettingsWindowOpenerService } from '@modules/settings-window/settings-window-opener.service';
import { map, of, switchMap, take } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { loadAudience } from './settings-window-deep-link.guard';

const FALLBACK_SETTINGS_PATH = 'general/account';

/** Keeps a refused settings address inside the window and over the same primary app. */
function accountRedirect(router: Router, stateUrl: string): RedirectCommand {
  const target = router.parseUrl(stateUrl);
  const primary = target.root.children[PRIMARY_OUTLET]?.segments.map(({ path }) => path) ?? [];

  /*
   * A cold entry IS the refused address, so replace it: otherwise Back returns to a URL that bounces
   * again — this time with `replaceUrl`, because popstate is browser-triggered — and the first press
   * appears to do nothing. In-app the refused URL was never written, so the entry behind is the app.
   */
  return new RedirectCommand(
    router.createUrlTree([{ outlets: { primary, settings: FALLBACK_SETTINGS_PATH.split('/') } }]),
    router.navigated ? undefined : { replaceUrl: true },
  );
}

/** The settings outlet's segments in a URL, wherever in the tree the window is mounted. */
function settingsSegments(group: UrlSegmentGroup): string[] | undefined {
  const settings = group.children?.['settings'];
  if (settings) return settings.segments.map(({ path }) => path);
  for (const child of Object.values(group.children ?? {})) {
    const found = settingsSegments(child);
    if (found) return found;
  }
  return undefined;
}

/**
 * An app settings address that has moved for this login (`settingsAppsAddress`): a team login's old `mail/…`,
 * `calendar`, `tasks` link opens its My apps page (`my/…`) with every sub-segment kept (`mail/signatures/<id>`); a
 * personal login's `my/…` opens its bare page. Over the same primary app, like the refusal below.
 */
function movedAppsRedirect(router: Router, stateUrl: string, teamLogin: boolean): RedirectCommand | undefined {
  const target = router.parseUrl(stateUrl);
  const segments = settingsSegments(target.root);
  const moved = segments && settingsAppsAddress(segments.join('/'), teamLogin);
  if (!moved) return undefined;
  const primary = target.root.children[PRIMARY_OUTLET]?.segments.map(({ path }) => path) ?? [];
  return new RedirectCommand(
    router.createUrlTree([{ outlets: { primary, settings: moved.split('/') } }], { queryParams: target.queryParams }),
    router.navigated ? undefined : { replaceUrl: true },
  );
}

/** Applies the menu's audience rule to every routed page in the settings window. */
export const settingsWindowAudienceGuard: CanActivateChildFn = (route, state) => {
  const accountService = inject(AccountService);
  const accountKind = inject(AccountKindService);
  const http = inject(HttpClient);
  const opener = inject(SettingsWindowOpenerService);
  const router = inject(Router);

  // A moved app address depends on the login's kind alone, so it moves before (and whatever) the audience resolves.
  // An unknown kind moves nothing: the address stays as it was until the login is known.
  const section = route.data['section'];
  const kind = accountKind.kind();
  if (kind && typeof section === 'string' && settingsAppsAddress(section, kind === 'team')) {
    const moved = movedAppsRedirect(router, state.url, kind === 'team');
    if (moved) return moved;
  }

  /*
   * The already-resolved account is the only input read per navigation: this guard re-runs on every
   * child activation, so a second request here would be a round-trip per click inside the window.
   * `checkoutRequired` needs no leg of its own — `checkoutRequiredGuard` on the ancestor layout route
   * (pages-routing.module.ts) is what a checkout-required account is stopped by, before the window.
   */
  return accountService.getAccount().pipe(
    take(1),
    switchMap((account) => {
      if (!account.id) {
        return of(true);
      }

      return loadAudience(http, account.id, accountKind.isTeam()).pipe(
        map((audience) => {
          if (!audience) {
            return true;
          }

          const section = route.data['section'];
          if (typeof section === 'string' && settingsPageAllowed(section, audience, (app) => opener.canSeeApp(app), accountKind.isTeam())) {
            return true;
          }
          if (section === FALLBACK_SETTINGS_PATH) {
            return true;
          }

          return accountRedirect(router, state.url);
        }),
      );
    }),
    catchError(() => of(true)),
  );
};
